<script setup lang="ts">
import { computed } from 'vue'
import { Check } from 'lucide-vue-next'

interface Stage {
  id?: string
  code: string
  title: string
  sequence?: number
  status?: string | null
  authorized?: boolean
  isAuthorized?: boolean
  canOpen?: boolean
}

const props = withDefaults(defineProps<{
  stages: readonly Stage[]
  currentIndex?: number
  currentStageId?: string | null
  orientation?: 'vertical' | 'horizontal'
  /** Mantido por compatibilidade; toda etapa autorizada continua abrível. */
  clickableCurrent?: boolean
}>(), {
  currentIndex: 0,
  currentStageId: null,
  orientation: 'vertical',
  clickableCurrent: true
})

const emit = defineEmits<{ select: [stageId: string] }>()

const orderedStages = computed(() => props.stages
  .map((stage, originalIndex) => ({ stage, originalIndex }))
  .sort((a, b) => (a.stage.sequence ?? a.originalIndex) - (b.stage.sequence ?? b.originalIndex))
  .map(({ stage }) => stage))

function stageId(stage: Stage): string { return stage.id ?? stage.code }
function isCompleted(stage: Stage): boolean {
  return ['completed', 'complete', 'done', 'finished'].includes((stage.status ?? '').toLowerCase())
}
function isInProgress(stage: Stage): boolean {
  return ['inprogress', 'in-progress', 'current', 'active'].includes((stage.status ?? '').toLowerCase())
}
function currentPosition(): number {
  if (props.currentStageId) {
    return orderedStages.value.findIndex((stage) => stageId(stage) === props.currentStageId)
  }
  const statusPosition = orderedStages.value.findIndex((stage) => isInProgress(stage))
  return statusPosition >= 0 ? statusPosition : props.currentIndex
}
function stateOf(stage: Stage, index: number): 'done' | 'current' | 'pending' {
  if (isCompleted(stage)) return 'done'
  if (props.currentStageId) return stageId(stage) === props.currentStageId ? 'current' : 'pending'
  if (index === currentPosition()) return 'current'
  return 'pending'
}
function canOpen(stage: Stage): boolean {
  return stage.authorized !== false && stage.isAuthorized !== false && stage.canOpen !== false
}
function stateLabel(stage: Stage, index: number): string {
  const state = stateOf(stage, index)
  if (state === 'done') return 'Concluída'
  if (state === 'current') return 'Etapa atual'
  return 'Pendente'
}
function onSelect(stage: Stage) {
  if (canOpen(stage)) emit('select', stageId(stage))
}
</script>

<template>
  <nav class="ck-stepper-nav" aria-label="Etapas da restauração">
    <ol class="ck-stepper" :class="`ck-stepper--${orientation}`">
      <li
        v-for="(stage, index) in orderedStages"
        :key="stageId(stage)"
        class="ck-stepper-item"
        :class="`is-${stateOf(stage, index)}`"
        :aria-posinset="index + 1"
        :aria-setsize="orderedStages.length"
      >
        <button
          type="button"
          class="ck-stepper-node"
          :disabled="!canOpen(stage)"
          :aria-current="stateOf(stage, index) === 'current' ? 'step' : undefined"
          :aria-disabled="!canOpen(stage) ? 'true' : undefined"
          :aria-label="`Etapa ${stage.sequence ?? index + 1}: ${stage.title}. ${stateLabel(stage, index)}${canOpen(stage) ? '. Abrir etapa' : '. Não autorizada'}`"
          @click="onSelect(stage)"
        >
          <span class="ck-stepper-dot" aria-hidden="true"><Check v-if="stateOf(stage, index) === 'done'" :size="14" /><template v-else>{{ stage.sequence ?? index + 1 }}</template></span>
          <span class="ck-stepper-label">{{ stage.title }}</span>
        </button>
        <span v-if="index < orderedStages.length - 1" class="ck-stepper-line" aria-hidden="true" />
      </li>
    </ol>
  </nav>
</template>

<style scoped>
.ck-stepper-nav { width: 100%; }
.ck-stepper { display: flex; margin: 0; padding: 0; list-style: none; }
.ck-stepper-node { display: flex; align-items: center; gap: var(--space-3); width: 100%; min-width: 44px; min-height: 44px; padding: var(--space-2) var(--space-3); border: 0; border-radius: var(--radius-md); background: none; font: inherit; color: inherit; cursor: pointer; text-align: left; }
.ck-stepper-node:hover { background: var(--ck-cream-200); }
.ck-stepper-node:disabled { cursor: not-allowed; opacity: .58; }
.ck-stepper-node:focus-visible { outline: 3px solid color-mix(in srgb, var(--ck-orange-500) 45%, transparent); outline-offset: 2px; }
.ck-stepper-dot { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 28px; height: 28px; border-radius: var(--radius-full); font-family: var(--font-mono); font-size: 12px; font-weight: var(--fw-bold); background: var(--ck-cream-200); color: var(--ck-text-faint); border: 1.5px solid var(--ck-cream-400); }
.is-done .ck-stepper-dot { background: var(--ck-ok-500); border-color: var(--ck-ok-500); color: white; }
.is-current .ck-stepper-dot { background: var(--ck-flame); border-color: transparent; color: white; box-shadow: var(--shadow-flame); }
.ck-stepper-label { font-size: var(--fs-sm); color: var(--ck-text-faint); }
.is-done .ck-stepper-label { color: var(--ck-text-muted); }
.is-current .ck-stepper-label { color: var(--ck-text); font-weight: var(--fw-bold); }

/* vertical: coluna, linha conectora à esquerda dos dots */
.ck-stepper--vertical { flex-direction: column; }
.ck-stepper--vertical .ck-stepper-item { position: relative; display: flex; flex-direction: column; }
.ck-stepper--vertical .ck-stepper-line { position: absolute; left: 25px; top: 36px; width: 2px; height: calc(100% - 28px + var(--space-3)); background: var(--ck-cream-300); }
.ck-stepper--vertical .ck-stepper-item.is-done .ck-stepper-line { background: var(--ck-ok-500); }
.ck-stepper--vertical .ck-stepper-item:not(:last-child) { padding-bottom: var(--space-2); }

/* horizontal: dots em linha com trilho abaixo, rótulo abaixo do dot */
.ck-stepper--horizontal { justify-content: space-between; align-items: flex-start; position: relative; }
.ck-stepper--horizontal .ck-stepper-item { display: flex; flex-direction: column; align-items: center; flex: 1; position: relative; }
.ck-stepper--horizontal .ck-stepper-node { flex-direction: column; justify-content: flex-start; gap: var(--space-2); min-height: 64px; padding: var(--space-2); text-align: center; }
.ck-stepper--horizontal .ck-stepper-label { text-align: center; font-size: 11px; line-height: 1.2; }
.ck-stepper--horizontal .ck-stepper-line { position: absolute; top: 22px; left: calc(50% + 20px); width: calc(100% - 40px); height: 2px; background: var(--ck-cream-300); }
.ck-stepper--horizontal .ck-stepper-item.is-done .ck-stepper-line { background: var(--ck-ok-500); }

@media (prefers-reduced-motion: reduce) {
  .ck-stepper-node { transition: none; }
}
</style>
